import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';
import DocumentPreview from './DocumentPreview';
import DocumentEditor from './DocumentEditor';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then((r) => r.json());
}

const DOC_TYPES = [
  { key: 'screening_memo', label: 'Screening Memo', icon: '🔍', desc: 'Condensed PowerPoint deck for the Tuesday/Thursday screening call. Generated from your Deal Notes snapshot. (8 slides)', pages: '8 slides', pptxOnly: true },
  { key: 'term_sheet', label: 'Term Sheet', icon: '📄', desc: 'Non-binding indication of interest with full loan terms (10 pages)', pages: '10' },
  { key: 'asr', label: 'Asset Summary Report', icon: '📊', desc: 'Comprehensive property and financial analysis for B-buyers (12-15 pages)', pages: '12-15' },
  { key: 'committee_memo', label: 'Committee Memo', icon: '🏛', desc: 'Internal credit committee presentation with downside analysis (18-22 pages)', pages: '18-22' },
  { key: 'one_pager', label: 'One-Pager', icon: '🗂', desc: 'Condensed, client-facing PowerPoint summary. Used for competitive bids on larger deals. (1 slide)', pages: '1 slide' },
];

export default function DocumentGen({ deal, onUpdate, onNavigate }) {
  const [docs, setDocs] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [editDocId, setEditDocId] = useState(null);

  useEffect(() => { loadDocs(); }, [deal.id]);

  const loadDocs = async () => {
    const res = await apiFetch(`/documents/${deal.id}`);
    if (res.success) setDocs(res.data);
  };

  const handleGenerate = async (docType) => {
    if (docType === 'screening_memo') {
      // Check for deal notes before trying
      const notesCheck = await apiFetch(`/deal-notes/${deal.id}`);
      if (!notesCheck.notes) {
        const goNow = window.confirm('Deal Notes not yet drafted.\n\nThe Screening Memo is generated from your Deal Notes snapshot.\n\nClick OK to go to Deal Notes now and draft them first.');
        if (goNow && onNavigate) onNavigate('deal_notes');
        return;
      }
    }
    setGenerating(docType);
    const res = await apiFetch(`/documents/generate/${deal.id}`, { method: 'POST', body: { doc_type: docType } });
    if (res.success) { await loadDocs(); onUpdate(); }
    else alert(res.error || 'Generation failed');
    setGenerating(null);
  };

  const handleExport = async (docId, format) => {
    setExporting(`${docId}-${format}`);
    const token = localStorage.getItem('cmbs_token');
    try {
      const res = await fetch(`${API_BASE}/documents/export/${docId}/${format}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `document.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json();
        alert(err.error || 'Export failed');
      }
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
    setExporting(null);
  };

  const handleApprove = async (docId) => {
    await apiFetch(`/documents/approve/${docId}`, { method: 'PUT' });
    await loadDocs();
  };

  const getLatest = (docType) => docs.find((d) => d.doc_type === docType);

  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24 };

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8 }}>Lender-Generated Documents</h3>
      <p style={{ color: COLORS.textLight, fontSize: 13, marginBottom: 20 }}>
        Generate lender-facing deal documents: term sheet, asset summary report, committee memo, and one-pager.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {DOC_TYPES.map((dt) => {
          const latest = getLatest(dt.key);
          const isGenerating = generating === dt.key;

          return (
            <div key={dt.key} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{dt.icon}</span>
                  <div>
                    <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 4 }}>{dt.label}</h4>
                    <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>{dt.desc}</p>
                  </div>
                </div>
                <span style={{ fontSize: 10, color: COLORS.textLight, background: '#F0F2F5', padding: '2px 8px', borderRadius: 10, flexShrink: 0, marginLeft: 8 }}>
                  {dt.pages}
                </span>
              </div>

              {latest && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: latest.approved ? '#E8F5E9' : '#E3F2FD',
                    color: latest.approved ? '#2E7D32' : '#1565C0',
                  }}>
                    v{latest.doc_version} {latest.approved ? 'Approved' : 'Ready for Review'}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button onClick={() => handleGenerate(dt.key)} disabled={isGenerating}
                  style={{ padding: '7px 14px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: isGenerating ? 0.6 : 1 }}>
                  {isGenerating ? 'Generating...' : latest ? 'Regenerate' : 'Generate'}
                </button>

                {latest && (
                  <>
                    <button onClick={() => setPreviewDocId(latest.id)}
                      style={{ padding: '7px 14px', background: '#fff', color: COLORS.navy, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      Preview
                    </button>
                    <button onClick={() => setEditDocId(latest.id)}
                      style={{ padding: '7px 14px', background: '#fff', color: COLORS.navy, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      Edit
                    </button>
                    {!latest.approved && (
                      <button onClick={() => handleApprove(latest.id)}
                        style={{ padding: '7px 14px', background: COLORS.success, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                        Approve
                      </button>
                    )}
                  </>
                )}
              </div>

              {latest && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
                  <button onClick={() => handleExport(latest.id, 'pptx')}
                    disabled={exporting === `${latest.id}-pptx`}
                    style={{ padding: '6px 14px', background: COLORS.navy, color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500, opacity: exporting === `${latest.id}-pptx` ? 0.6 : 1 }}>
                    {exporting === `${latest.id}-pptx` ? 'Exporting...' : 'Export PowerPoint'}
                  </button>
                  <button onClick={() => handleExport(latest.id, 'pdf')}
                    disabled={exporting === `${latest.id}-pdf`}
                    style={{ padding: '6px 14px', background: '#C62828', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500, opacity: exporting === `${latest.id}-pdf` ? 0.6 : 1 }}>
                    {exporting === `${latest.id}-pdf` ? 'Exporting...' : 'Export PDF'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {previewDocId && <DocumentPreview docId={previewDocId} onClose={() => setPreviewDocId(null)} />}
      {editDocId && <DocumentEditor docId={editDocId} onClose={() => setEditDocId(null)} onSave={loadDocs} />}
    </div>
  );
}
